import { parseOCC, formatOCC } from './occParser.js'
import { computeEquityLotAccounting } from './positionLotAccounting.js'

const STOCK_TYPES = new Set(['equity', 'etf', 'collective_investment', 'mutual_fund'])
const number = value => value == null || value === '' || !Number.isFinite(Number(value)) ? null : Number(value)
const cents = value => Math.round((value + Number.EPSILON) * 100)
const sumMoney = values => values.some(value => value == null) ? null : values.reduce((sum, value) => sum + cents(value), 0) / 100
const dateKey = value => /^\d{4}-\d{2}-\d{2}/.test(value || '') && Number.isFinite(Date.parse(value)) ? value.slice(0, 10) : null

export function historyUnderlying(symbol) {
  const normalized = String(symbol || '').trim().toUpperCase()
  return parseOCC(normalized)?.underlying || normalized
}

export function historyContractName(symbol) {
  const option = parseOCC(symbol)
  return option ? formatOCC(symbol).replace(/ C$/, ' Call').replace(/ P$/, ' Put') : String(symbol || '').trim()
}

function partialRows(trade, executions, base) {
  const unavailable = () => [{ ...base, id: `${trade.id}:partial`, date: null, net: null, gross: null, costs: null, quantity: null, status: 'Partial exits unavailable', accounting: 'Execution history does not reconcile to the remaining quantity.' }]
  // Reuse the existing workspace's reconciled long-equity LIFO contract. Do not
  // invent short/option assignment or partial-lot accounting from average cost.
  if (base.kind !== 'stock' || !['long', 'buy'].includes(String(trade.side).toLowerCase())) return unavailable()
  const rows = [...new Map(executions.filter(e => e.trade_id === trade.id).map(e => [e.id, e])).values()]
    .sort((a, b) => Date.parse(a.executed_at) - Date.parse(b.executed_at) || String(a.id).localeCompare(String(b.id)))
  if (!rows.length || rows.some(e => !e.id || e.symbol !== trade.symbol || e.account_number !== trade.account_number
    || !dateKey(e.executed_at) || !(number(e.quantity) > 0) || !(number(e.price) > 0)
    || number(e.commission) == null || number(e.fees) == null
    || !(e.execution_type === 'entry' && ['buy', 'buy_to_open'].includes(String(e.side).toLowerCase())
      || ['exit', 'partial_exit'].includes(e.execution_type) && ['sell', 'sell_to_close'].includes(String(e.side).toLowerCase())))) return unavailable()
  const full = computeEquityLotAccounting(rows, { symbol: trade.symbol, method: 'LIFO' })
  const entered = rows.filter(e => e.execution_type === 'entry').reduce((sum, e) => sum + Number(e.quantity), 0)
  if (number(trade.quantity_total) == null || number(trade.quantity_open) == null || !full.available || full.unmatchedExitQty !== 0
    || Math.abs(full.remainingQty - Number(trade.quantity_open)) > .0001
    || Math.abs(entered - Number(trade.quantity_total)) > .0001) return unavailable()
  const result = []
  let previous = 0
  rows.forEach((e, i) => {
    if (e.execution_type === 'entry') return
    const accounting = computeEquityLotAccounting(rows.slice(0, i + 1), { symbol: trade.symbol, method: 'LIFO' })
    result.push({ ...base, id: `${trade.id}:${e.id}`, date: dateKey(e.executed_at), net: (cents(accounting.realizedPnl) - cents(previous)) / 100,
      gross: null, costs: null, quantity: Number(e.quantity), exitPrice: Number(e.price), status: 'Partial exit', accounting: 'LIFO matched lots; includes allocated entry and exit costs.' })
    previous = accounting.realizedPnl
  })
  return result.length ? result : unavailable()
}

export function buildPositionHistory(trades, executions = [], symbol) {
  const underlying = historyUnderlying(symbol)
  const rows = []
  for (const trade of new Map((trades || []).filter(t => t.id).map(t => [t.id, t])).values()) {
    if (historyUnderlying(trade.symbol) !== underlying || trade.status === 'duplicate') continue
    const kind = parseOCC(trade.symbol) ? 'options' : STOCK_TYPES.has(String(trade.asset_type).toLowerCase()) ? 'stock' : null
    if (!kind) continue
    const base = { id: trade.id, tradeId: trade.id, kind, symbol: trade.symbol, name: historyContractName(trade.symbol), account: trade.account_number,
      entryDate: dateKey(trade.entry_date), entryPrice: number(trade.avg_entry_price), exitPrice: number(trade.avg_exit_price) }
    if (trade.status === 'closed') {
      rows.push({ ...base, date: dateKey(trade.exit_date), quantity: number(trade.quantity_total), gross: number(trade.gross_pnl),
        costs: sumMoney([number(trade.commission), number(trade.fees)]), net: number(trade.net_pnl), status: 'Closed', accounting: 'Recorded Trade Log net P&L; fees are already included.' })
    } else if (trade.status === 'partial' || (trade.status === 'open' && number(trade.quantity_open) != null && number(trade.quantity_total) > number(trade.quantity_open))) {
      rows.push(...partialRows(trade, executions, base))
    }
  }
  return rows.sort((a, b) => (b.date || '').localeCompare(a.date || '') || a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
}

export function summarizePositionHistory(rows, year = 'all') {
  // Unknown dates remain visible and make year totals unavailable: excluding
  // them would silently claim a complete total for the selected period.
  const selected = rows.filter(row => year === 'all' || !row.date || row.date.startsWith(`${year}-`))
  const total = kind => sumMoney(selected.filter(row => !kind || row.kind === kind).map(row => year !== 'all' && !row.date ? null : row.net))
  return { rows: selected, stock: total('stock'), options: total('options'), total: total(),
    unavailable: selected.filter(row => row.net == null || year !== 'all' && !row.date).length,
    years: [...new Set(rows.map(row => row.date?.slice(0, 4)).filter(Boolean))].sort().reverse() }
}

const TRADE_FIELDS = 'id,symbol,asset_type,status,side,account_number,entry_date,exit_date,quantity_total,quantity_open,avg_entry_price,avg_exit_price,gross_pnl,commission,fees,net_pnl'
const EXECUTION_FIELDS = 'id,trade_id,symbol,account_number,execution_type,side,quantity,price,commission,fees,executed_at'
async function pages(query, signal) {
  const rows = []
  for (let offset = 0; offset < 50000; offset += 500) {
    if (signal?.aborted) throw new Error('History request cancelled')
    const { data, error } = await query().order('id', { ascending: true }).range(offset, offset + 499).abortSignal(signal)
    if (error) throw new Error(error.message || 'History unavailable')
    if (!Array.isArray(data)) throw new Error('History response unavailable')
    rows.push(...data)
    if (data.length < 500) return rows
  }
  throw new Error('History exceeds the supported page limit')
}

export async function loadPositionHistory(client, userId, symbol, signal = new AbortController().signal) {
  const underlying = historyUnderlying(symbol)
  if (!userId || !/^[A-Z0-9.\/-]{1,12}$/.test(underlying)) throw new Error('History is unavailable for this asset')
  // Prefix is only a query optimization. Exact parsed underlying matching below
  // prevents ticker-prefix collisions and mixing unrelated option roots.
  const trades = (await pages(() => client.from('trades').select(TRADE_FIELDS).eq('user_id', userId).ilike('symbol', `${underlying}%`).neq('status', 'duplicate'), signal))
    .filter(trade => historyUnderlying(trade.symbol) === underlying)
  const partialIds = trades.filter(t => ['partial', 'open'].includes(t.status) && (t.status === 'partial' || number(t.quantity_total) > number(t.quantity_open))).map(t => t.id)
  const executions = []
  for (let i = 0; i < partialIds.length; i += 50) {
    executions.push(...await pages(() => client.from('executions').select(EXECUTION_FIELDS).eq('user_id', userId).in('trade_id', partialIds.slice(i, i + 50)), signal))
  }
  return buildPositionHistory(trades, executions, underlying)
}
